import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import { broadcastAngularEvent, onAngularEvent } from '../../services/angular-bridge';
import { reportTaskComplete, reportTaskProgress } from '../../services/taskProgress';
import { notifySuccess } from '../../services/notify';
import { refreshBatchReviewMetrics } from '../useReview';

export interface Tag {
  id: string;
  projectId: string;
  categoryId: string;
  name: string;
  color: string;
  isNameEditable: boolean;
  numResources: number | null;
  assignees: unknown | null;
  parentId: string | null;
  status: string | null;
  users: unknown | null;
  roles: unknown | null;
  inverses?: Array<string | { id: string }>;
  exclusions?: Array<string | { id: string }>;
  children?: Array<string | { id: string }>;
  inverseObjects?: { id: string; name: string }[];
  exclusionObjects?: { id: string; name: string }[];
  childrenObjects?: { id: string; name: string }[];
}

export interface TagInput {
  name: string;
  color: string;
  categoryId: string;
  projectId: string;
  inverses: string[];
  exclusions: string[];
  children: string[];
}

export interface TagPage {
  pageNumber: number;
  totalPages: number;
  numberTags: number;
  totalTags: number;
  tags: Tag[];
}

export function useProjectTags(projectId: string, categoryId: string | null, page: number) {
  return useQuery<TagPage>({
    queryKey: ['projectTags', projectId, categoryId, page],
    queryFn: () =>
      api.get<TagPage>(`/project/${projectId}/tag?assignedOnly=false&categoryId=${categoryId}&page=${page}&limit=20`),
    enabled: !!projectId && !!categoryId,
    // Source of truth for tag relationships (/tags/:id returns them null) — never serve stale.
    staleTime: 0,
    // Keep the current page's rows on screen while the next page loads so the
    // table doesn't blank out into the "no tags found" warning between fetches.
    placeholderData: keepPreviousData,
  });
}

// Resolve a single tag by name (e.g. the built-in "Reviewed" tag). Mirrors Angular
// TagService.FindByName. Disabled until a name is provided.
export function useTagByName(projectId: string, name: string | null) {
  return useQuery<Tag>({
    queryKey: ['tagByName', projectId, name],
    queryFn: () => api.get<Tag>(`/project/${projectId}/tag/name/${name}`),
    enabled: !!projectId && !!name,
  });
}

export function useTagsByIds(projectId: string, ids: string[]) {
  return useQuery<Tag[]>({
    queryKey: ['tags', projectId, ids],
    queryFn: () => api.get<Tag[]>(`/project/${projectId}/tags/${ids.join(',')}`),
    enabled: !!projectId && ids.length > 0,
  });
}

export function useDeleteTag(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tag: { id: string; name: string }) => api.delete(`/project/${projectId}/tag/${tag.id}`),
    onSuccess: (_data, tag) => {
      notifySuccess({ title: 'Untagging Started', description: `Deleting tag ${tag.name}. We will notify you when it completes.`, timeout: 2500 });
      queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useCreateTag(projectId: string) {
  return useToastMutation<Tag, TagInput>(
    ['projectTags', projectId],
    (input) => api.post<Tag>(`/project/${projectId}/tag`, input),
    {
      success: (data, v) => ({ title: 'Tag added successfully', description: `Name: ${data?.name ?? v.name}`, timeout: 5000 }),
    },
  );
}

export function useReprocessTag(projectId: string) {
  return useToastMutation(
    ['projectTags', projectId],
    (tagId: string) => api.post(`/project/${projectId}/tag/${tagId}/reprocess`),
    {
      success: () => ({ title: 'Reprocessing started', description: '', timeout: 2500 }),
    },
  );
}

export function useUpdateTag(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<Tag, Error, Tag>({
    mutationFn: (input) => api.put<Tag>(`/project/${projectId}/tag/${input.id}`, input),
    onSuccess: (data, variables) => {
      notifySuccess({ title: 'Tag updated successfully', description: `Name: ${data?.name ?? variables.name}`, timeout: 5000 });
      queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['tagForm', projectId] });
      queryClient.invalidateQueries({ queryKey: ['tagFormForCurrentUser', projectId] });
    },
  });
}

export interface TaggingMessage {
  projectId: string;
  id: string;
  name?: string;
  description?: string;
  percentageComplete?: number;
}

/**
 * Every tagging websocket event: the header task dropdown, completion toasts and the tag
 * refresh. Mounted once by WebSocketBridge — bulk tagging is started from the Angular review
 * grid, nowhere near the Tags tab, so a tab-scoped listener would be unmounted exactly when
 * it is needed.
 *
 * TaggingComplete arrives BEFORE the trailing 100% TaggingUpdate. reportTaskProgress drops
 * frames at >=100%, which is what stops that trailing frame re-adding a task the completion
 * already removed.
 */
export function useTaggingEvents() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const finish = (title: string) => (message: TaggingMessage) => {
      notifySuccess({ title, description: `${message.description} has completed.` });
      reportTaskComplete(message.id);

      if (message.projectId) {
        queryClient.invalidateQueries({ queryKey: ['projectTags', message.projectId] });
        queryClient.invalidateQueries({ queryKey: ['project', message.projectId] });
        refreshBatchReviewMetrics(queryClient, message.projectId);
      }

      // Still-Angular consumers (ResourceController, reviewStats) refresh off these two.
      // They used to come from TagService's own listener, which React has now taken over.
      broadcastAngularEvent('tags.fetch');
      broadcastAngularEvent('tags.complete');
    };

    const offUpdate = onAngularEvent('FeedService.TaggingUpdate', (message: TaggingMessage) => {
      if (!message?.projectId || typeof message.percentageComplete !== 'number') return;
      const label = message.description || message.name || 'Tagging';
      reportTaskProgress({
        id: message.id,
        name: label,
        description: label,
        percentageComplete: message.percentageComplete,
      });
    });

    const offTagging = onAngularEvent('FeedService.TaggingComplete', finish('Tagging complete'));
    const offUntagging = onAngularEvent('FeedService.UntaggingComplete', finish('Untagging complete'));

    return () => { offUpdate(); offTagging(); offUntagging(); };
  }, [queryClient]);
}
